import React, { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@artstorefronts/ui';
import { X, ChevronsUpDown } from 'lucide-react';
import { LOCATION_MAX_LENGTH } from '@arthelper/shared/profile-fields';

import {
  SEARCH_COUNTRIES,
  SEARCH_STATES,
  SEARCH_CITIES,
  GET_ALL_COUNTRIES,
  GET_STATES,
  GET_CITIES,
} from '../../graphql/queries';

interface Country {
  isoCode: string;
  name: string;
  phonecode: string;
  flag: string;
  currency: string;
  latitude: string;
  longitude: string;
  timezones: Array<{
    zoneName: string;
    gmtOffset: number;
    gmtOffsetName: string;
    abbreviation: string;
    tzName: string;
  }>;
}

interface State {
  name: string;
  isoCode: string;
  countryCode: string;
  // Nullable on the schema: the dataset ships states with no geo data. See the
  // State type in api/src/graphql/typeDefs/location.ts.
  latitude: string | null;
  longitude: string | null;
}

interface City {
  name: string;
  countryCode: string;
  stateCode: string;
  latitude: string;
  longitude: string;
}

/**
 * A coordinate the profile can actually store, or `null`.
 *
 * `State.latitude` / `State.longitude` are nullable on the schema, and a
 * free-typed city has no coordinates at all — a bare `parseFloat` turns both
 * into `NaN`, which then rides into the profile as a broken location.
 *
 * Explicitly `null`, never `undefined`: the caller feeds this straight to
 * react-hook-form's `setValue`, and `undefined` is the one value RHF cannot be
 * relied on to treat as a clear — a stale pair surviving the save would satisfy
 * `geolocateProfile`'s "already has location" guard and block the IP backfill
 * this flow depends on. `LATITUDE_SCHEMA` accepts null, so null it is.
 */
function coordinate(value: string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = parseFloat(value);

  return Number.isFinite(parsed) ? parsed : null;
}

interface LocationSelectorProps {
  country: string;
  region: string;
  city: string;
  onCountryChange: (country: Country | null) => void;
  onRegionChange: (region: State | null) => void;
  onCityChange: (city: City | null) => void;
  onLocationDataChange: (data: {
    countryCode?: string | null;
    country?: string | null;
    regionCode?: string | null;
    region?: string | null;
    city?: string | null;
    latitude?: number | null;
    longitude?: number | null;
  }) => void;
  className?: string;
}

export const LocationSelector: React.FC<LocationSelectorProps> = ({
  country,
  region,
  city,
  onCountryChange,
  onRegionChange,
  onCityChange,
  onLocationDataChange,
  className = '',
}) => {
  const { t } = useTranslation('settings');

  // State for dropdown visibility
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showStateDropdown, setShowStateDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  // State for search inputs
  const [countrySearch, setCountrySearch] = useState('');
  const [stateSearch, setStateSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');

  // State for selected items
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [selectedState, setSelectedState] = useState<State | null>(null);
  const [selectedCity, setSelectedCity] = useState<City | null>(null);

  // GraphQL queries
  const [searchCountries, { data: countriesData, loading: searchCountriesLoading }] = useLazyQuery(SEARCH_COUNTRIES);
  const [searchStates, { data: statesData, loading: searchStatesLoading }] = useLazyQuery(SEARCH_STATES);
  const [searchCities, { data: citiesData, loading: searchCitiesLoading }] = useLazyQuery(SEARCH_CITIES);
  const [getAllCountries, { data: allCountriesData, loading: getAllCountriesLoading }] =
    useLazyQuery(GET_ALL_COUNTRIES);
  const [getStates, { data: allStatesData, loading: getStatesLoading }] = useLazyQuery(GET_STATES);
  const [getCities, { data: allCitiesData, loading: getCitiesLoading }] = useLazyQuery(GET_CITIES);

  // Search results - use search results when searching, otherwise use all results
  const allCountries = allCountriesData?.countries || [];
  const countries =
    countriesData?.searchCountries ||
    (allCountries.length > 0 && countrySearch.length >= 2
      ? allCountries.filter((country: Country) => country.name.toLowerCase().includes(countrySearch.toLowerCase()))
      : allCountries);
  const allStates = allStatesData?.states || [];
  const states =
    statesData?.searchStates ||
    (allStates.length > 0 && stateSearch.length >= 2
      ? allStates.filter((state: State) => state.name.toLowerCase().includes(stateSearch.toLowerCase()))
      : allStates);

  const allCities = allCitiesData?.cities || [];
  const cities =
    citiesData?.searchCities ||
    (allCities.length > 0 && citySearch.length >= 2
      ? allCities.filter((city: City) => city.name.toLowerCase().includes(citySearch.toLowerCase()))
      : allCities);

  // Loading states - only show loading when we don't have local data
  const isCountriesLoading =
    (searchCountriesLoading || getAllCountriesLoading) && allCountriesData?.countries?.length === 0;
  const isStatesLoading = (searchStatesLoading || getStatesLoading) && allStatesData?.states?.length === 0;
  const isCitiesLoading = (searchCitiesLoading || getCitiesLoading) && allCitiesData?.cities?.length === 0;

  // A region whose FULL city list came back empty — Belgium's provinces carry
  // none, and so do 1029 of the dataset's 5308 states. Gated on the query having
  // actually resolved (`allCitiesData` is undefined while it is in flight, so
  // `isCitiesLoading` cannot be trusted here) and on the unfiltered list, so a
  // typo inside a region that DOES have cities still says "no city found"
  // instead of offering to store the typo.
  const hasNoCityList = !!selectedState && !getCitiesLoading && !!allCitiesData && allCities.length === 0;

  // Load initial data when component mounts with pre-filled values
  useEffect(() => {
    // Always load countries data
    getAllCountries();

    // Load states data if we have a country
    if (country) {
      // We'll need to find the country first, then load states
      // This will be handled in the initialization effect below
    }
  }, []); // Only run once on mount

  // Initialize selected values from props when component mounts
  useEffect(() => {
    if (country && !selectedCountry) {
      setCountrySearch(country);
      // Try to find and set the selected country object
      if (allCountries.length > 0) {
        const foundCountry = allCountries.find((c: Country) => c.name === country);
        if (foundCountry) {
          setSelectedCountry(foundCountry);
          // Now load states for this country
          getStates({ variables: { countryCode: foundCountry.isoCode } });
        }
      }
    }
    if (region && !selectedState && selectedCountry) {
      setStateSearch(region);
      // Try to find and set the selected state object
      if (allStates.length > 0) {
        const foundState = allStates.find((s: State) => s.name === region);
        if (foundState) {
          setSelectedState(foundState);
          // Now load cities for this state
          getCities({
            variables: {
              countryCode: selectedCountry.isoCode,
              stateCode: foundState.isoCode,
            },
          });
        }
      }
    }
    if (city && !selectedCity && selectedCountry && selectedState) {
      setCitySearch(city);
      // Try to find and set the selected city object
      if (allCities.length > 0) {
        const foundCity = allCities.find((c: City) => c.name === city);
        if (foundCity) {
          setSelectedCity(foundCity);
        }
      }
    }
  }, [
    country,
    region,
    city,
    selectedCountry,
    selectedState,
    selectedCity,
    allCountries,
    allStates,
    allCities,
    getStates,
    getCities,
  ]);

  // A parent level's keys go out only when it is resolved: an unresolved parent
  // (a stored name the dataset lacks, or countries still loading) must not be
  // sent as null, or clearing a city would also erase the country (issue #4776).
  const parentKeys = (country: Country | null, state?: State | null) => ({
    ...(country ? { countryCode: country.isoCode, country: country.name } : {}),
    ...(state ? { regionCode: state.isoCode, region: state.name } : {}),
  });

  // Simple handlers
  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setCountrySearch(country.name);
    setShowCountryDropdown(false);
    onCountryChange(country);

    // Clear dependent fields
    setSelectedState(null);
    setSelectedCity(null);
    setStateSearch('');
    setCitySearch('');
    onRegionChange(null);
    onCityChange(null);

    onLocationDataChange({
      ...parentKeys(country),
      regionCode: null,
      region: null,
      city: null,
      latitude: coordinate(country.latitude),
      longitude: coordinate(country.longitude),
    });
  };

  const handleStateSelect = (state: State) => {
    setSelectedState(state);
    setStateSearch(state.name);
    setShowStateDropdown(false);
    onRegionChange(state);

    // Clear city
    setSelectedCity(null);
    setCitySearch('');
    onCityChange(null);

    onLocationDataChange({
      ...parentKeys(selectedCountry, state),
      city: null,
      latitude: coordinate(state.latitude),
      longitude: coordinate(state.longitude),
    });
  };

  const handleCitySelect = (city: City) => {
    setSelectedCity(city);
    setCitySearch(city.name);
    setShowCityDropdown(false);
    onCityChange(city);

    onLocationDataChange({
      ...parentKeys(selectedCountry, selectedState),
      city: city.name,
      latitude: coordinate(city.latitude),
      longitude: coordinate(city.longitude),
    });
  };

  // Belgium's cities hang off its three REGIONS, none off its ten PROVINCES, so
  // picking Antwerp or Limburg returns an empty list. Rather than a dead end, the
  // typed name is accepted as-is — with NO coordinates, deliberately: inheriting
  // the province's would pin the profile at province precision forever, because
  // geolocateProfile only backfills a profile whose lat AND lng are both null.
  const handleFreeTextCity = (name: string) => {
    const typed = name.trim();
    // A city is only meaningful under its own country and region — writing one
    // without them is the name/code drift the shared location patch exists to
    // prevent. The dropdown that calls this is already gated on a selected
    // region; this keeps the invariant local to the write.
    if (!typed || typed.length > LOCATION_MAX_LENGTH || !selectedCountry || !selectedState) return;

    const freeTextCity: City = {
      name: typed,
      countryCode: selectedCountry.isoCode,
      stateCode: selectedState.isoCode,
      latitude: '',
      longitude: '',
    };

    setSelectedCity(freeTextCity);
    setCitySearch(typed);
    setShowCityDropdown(false);
    onCityChange(freeTextCity);

    onLocationDataChange({
      countryCode: selectedCountry.isoCode,
      country: selectedCountry.name,
      regionCode: selectedState.isoCode,
      region: selectedState.name,
      // Explicit null, not undefined — see `coordinate` above.
      city: typed,
      latitude: null,
      longitude: null,
    });
  };

  // The clear handlers used to send no coordinate pair at all, which reaches
  // react-hook-form as `undefined` — the one value it cannot be relied on to
  // treat as a clear, so the previous selection's pair could survive the save and
  // leave a profile with coordinates it no longer has a place for. Each of them
  // now states the pair the remaining selection anchors on, explicitly — and an
  // explicit null for every name field it clears, for the same reason: the admin
  // save forwards the form raw, and Apollo drops `undefined` keys, so a cleared
  // country never reached the server (issue #4776).
  const handleClearCountry = () => {
    setSelectedCountry(null);
    setCountrySearch('');
    setShowCountryDropdown(false);
    onCountryChange(null);

    setSelectedState(null);
    setSelectedCity(null);
    setStateSearch('');
    setCitySearch('');
    onRegionChange(null);
    onCityChange(null);

    onLocationDataChange({
      countryCode: null,
      country: null,
      regionCode: null,
      region: null,
      city: null,
      latitude: null,
      longitude: null,
    });
  };

  const handleClearState = () => {
    setSelectedState(null);
    setStateSearch('');
    setShowStateDropdown(false);
    onRegionChange(null);

    setSelectedCity(null);
    setCitySearch('');
    onCityChange(null);

    onLocationDataChange({
      ...parentKeys(selectedCountry),
      regionCode: null,
      region: null,
      city: null,
      latitude: coordinate(selectedCountry?.latitude),
      longitude: coordinate(selectedCountry?.longitude),
    });
  };

  const handleClearCity = () => {
    setSelectedCity(null);
    setCitySearch('');
    setShowCityDropdown(false);
    onCityChange(null);

    onLocationDataChange({
      ...parentKeys(selectedCountry, selectedState),
      city: null,
      latitude: coordinate(selectedState?.latitude),
      longitude: coordinate(selectedState?.longitude),
    });
  };

  // Search effects with default loading
  useEffect(() => {
    if (countrySearch.length >= 2 && allCountriesData?.countries?.length === 0) {
      // Only search API if we don't have local data
      searchCountries({ variables: { query: countrySearch } });
    } else if (countrySearch.length === 0 && showCountryDropdown) {
      // Show all countries when search is empty and dropdown is open
      getAllCountries();
    }
  }, [countrySearch, searchCountries, getAllCountries, showCountryDropdown, allCountriesData]);

  // Handle when user clears the search input manually
  useEffect(() => {
    if (showCountryDropdown && countrySearch === '' && !countriesData?.searchCountries) {
      getAllCountries();
    }
  }, [showCountryDropdown, countrySearch, countriesData, getAllCountries]);

  useEffect(() => {
    if (stateSearch.length >= 2 && selectedCountry && allStatesData?.states?.length === 0) {
      // Only search API if we don't have local data
      searchStates({
        variables: {
          countryCode: selectedCountry.isoCode,
          query: stateSearch,
        },
      });
    } else if (stateSearch.length === 0 && selectedCountry && allStatesData?.states?.length === 0) {
      // Show all states when search is empty and we don't have local data
      getStates({ variables: { countryCode: selectedCountry.isoCode } });
    }
  }, [stateSearch, selectedCountry, searchStates, getStates, allStatesData]);

  useEffect(() => {
    // A region with no city list has nothing to fetch and nothing to search, and
    // BOTH calls below would re-enter loading — which flips `hasNoCityList` back
    // off and pulls the typed-city offer out from under the user mid-keystroke.
    // The dropdown-open effect already fetched the (empty) list once; this one
    // bows out entirely. Guarded up front so neither branch can slip through.
    if (hasNoCityList || !selectedCountry || !selectedState) {
      return;
    }

    if (citySearch.length >= 2 && allCitiesData?.cities?.length === 0) {
      // Only search API if we don't have local data
      searchCities({
        variables: {
          countryCode: selectedCountry.isoCode,
          stateCode: selectedState.isoCode,
          query: citySearch,
        },
      });
      return;
    }

    if (citySearch.length === 0 && allCitiesData?.cities?.length === 0) {
      // Show all cities when search is empty and we don't have local data
      getCities({
        variables: {
          countryCode: selectedCountry.isoCode,
          stateCode: selectedState.isoCode,
        },
      });
    }
  }, [citySearch, selectedCountry, selectedState, searchCities, getCities, allCitiesData, hasNoCityList]);

  // Load all options when dropdown opens
  useEffect(() => {
    if (showCountryDropdown) {
      getAllCountries();
    }
  }, [showCountryDropdown, getAllCountries]);

  useEffect(() => {
    if (showStateDropdown && selectedCountry) {
      getStates({ variables: { countryCode: selectedCountry.isoCode } });
    }
  }, [showStateDropdown, selectedCountry, getStates]);

  useEffect(() => {
    if (showCityDropdown && selectedCountry && selectedState) {
      getCities({
        variables: {
          countryCode: selectedCountry.isoCode,
          stateCode: selectedState.isoCode,
        },
      });
    }
  }, [showCityDropdown, selectedCountry, selectedState, getCities]);

  // Capped at `CITY_SCHEMA`'s own limit — offering a name the write would refuse
  // buys the user a save failure with nothing to show for it.
  const typedCity = citySearch.trim();
  const canUseTypedCity = hasNoCityList && typedCity.length > 0 && typedCity.length <= LOCATION_MAX_LENGTH;

  // Determine if inputs should be enabled
  const isStateEnabled = selectedCountry || countrySearch;
  const isCityEnabled = (selectedCountry || countrySearch) && (selectedState || stateSearch);

  return (
    <div className={`location-selector space-y-4 ${className}`}>
      {/* Horizontal layout for location fields */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Country Field */}
        <div className="relative">
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('profile.location.country')}</label>
          <Popover open={showCountryDropdown} onOpenChange={setShowCountryDropdown}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={showCountryDropdown}
                className="w-full justify-between"
              >
                {selectedCountry ? (
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{selectedCountry.flag}</span>
                    <span>{selectedCountry.name}</span>
                  </div>
                ) : (
                  countrySearch || t('profile.location.select-country')
                )}
                {!selectedCountry && !countrySearch && <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
              <Command>
                <CommandInput
                  placeholder={t('profile.location.search-country')}
                  value={countrySearch}
                  onValueChange={setCountrySearch}
                />
                <CommandList>
                  {isCountriesLoading ? (
                    <CommandEmpty>{t('profile.location.loading-countries')}</CommandEmpty>
                  ) : (
                    <>
                      <CommandEmpty>{t('profile.location.no-country')}</CommandEmpty>
                      <CommandGroup>
                        {countries.map((country: Country) => (
                          <CommandItem
                            key={country.isoCode}
                            value={country.name}
                            onSelect={() => handleCountrySelect(country)}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{country.flag}</span>
                              <span>{country.name}</span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {(selectedCountry || countrySearch) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClearCountry}
              className="absolute right-2 top-6 size-6 p-0 hover:bg-gray-100"
            >
              <X className="size-4" />
            </Button>
          )}
        </div>

        {/* State/Region Field */}
        <div className="relative">
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('profile.location.state-region')}</label>
          <Popover open={showStateDropdown} onOpenChange={setShowStateDropdown}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={showStateDropdown}
                className="w-full justify-between"
                disabled={!isStateEnabled}
              >
                {selectedState ? selectedState.name : stateSearch || t('profile.location.select-state')}
                {!selectedState && !stateSearch && <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
              <Command>
                <CommandInput
                  placeholder={t('profile.location.search-state')}
                  value={stateSearch}
                  onValueChange={setStateSearch}
                />
                <CommandList>
                  {isStatesLoading ? (
                    <CommandEmpty>{t('profile.location.loading-states')}</CommandEmpty>
                  ) : (
                    <>
                      <CommandEmpty>{t('profile.location.no-state')}</CommandEmpty>
                      <CommandGroup>
                        {states.map((state: State) => (
                          <CommandItem key={state.isoCode} value={state.name} onSelect={() => handleStateSelect(state)}>
                            {state.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {(selectedState || stateSearch) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClearState}
              className="absolute right-2 top-6 size-6 p-0 hover:bg-gray-100"
            >
              <X className="size-4" />
            </Button>
          )}
        </div>

        {/* City Field */}
        <div className="relative">
          <label className="block text-xs font-medium text-gray-700 mb-1">{t('profile.location.city-label')}</label>
          <Popover open={showCityDropdown} onOpenChange={setShowCityDropdown}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={showCityDropdown}
                className="w-full justify-between"
                disabled={!isCityEnabled}
              >
                {selectedCity ? selectedCity.name : citySearch || t('profile.location.select-city')}
                {!selectedCity && !citySearch && <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
              <Command>
                <CommandInput
                  placeholder={t('profile.location.search-city')}
                  value={citySearch}
                  onValueChange={setCitySearch}
                />
                <CommandList>
                  {/* Outside the loading branch on purpose. For a region with no
                      city list, `isCitiesLoading` is true for the whole of every
                      keystroke's search request, so rendering the typed-city offer
                      inside that branch unmounted it exactly while the user was
                      typing into it. There is nothing to load here either, which
                      is why `hasNoCityList` suppresses the loading message too. */}
                  {canUseTypedCity && (
                    <CommandGroup>
                      <CommandItem value={citySearch} onSelect={() => handleFreeTextCity(typedCity)}>
                        {t('profile.location.use-typed-city', { city: typedCity })}
                      </CommandItem>
                    </CommandGroup>
                  )}
                  {isCitiesLoading && !hasNoCityList ? (
                    <CommandEmpty>{t('profile.location.loading-cities')}</CommandEmpty>
                  ) : (
                    <>
                      <CommandEmpty>{t('profile.location.no-city')}</CommandEmpty>
                      <CommandGroup>
                        {cities.map((city: City) => (
                          <CommandItem
                            key={`${city.countryCode}-${city.stateCode}-${city.name}`}
                            value={city.name}
                            onSelect={() => handleCitySelect(city)}
                          >
                            {city.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
          {(selectedCity || citySearch) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClearCity}
              className="absolute right-2 top-6 size-6 p-0 hover:bg-gray-100"
            >
              <X className="size-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
